import { AbstractModel } from '@/models/abstractModel';
import { IExpectedResult } from '@/types/bet';
import {
    BetStatus,
    BetType,
    BookmakerCode,
    BOT_CONDITIONS_MAP_REVERSE,
    BOT_OPTIONS_MAP_REVERSE,
    DeviceType,
    EventPeriod,
    Jollywhere,
    MarketGroup,
    MarketType,
    Teamwhere,
    TradingType,
    WL,
} from '@/constants';
import { betStatusDictionary, useBookmakerDictionary, useBotDictionary } from '@/stores';
import { BotDictionaryItem } from '@/models/dictionary/bot';
import { BookmakerDictionaryItem } from '@/models/dictionary/bookmaker';
import { AppError } from '@/errors/appError';
import { newBetMessage, newBetWaitingMessage } from '@/helpers/message';
import { dateFormatInUserTimeZone, formatOdds, londonToGMT, playSound } from '@/helpers';
import { useUserStore } from '@/stores/user';
import { usePlatform } from '@/composables/usePlatform';
import { parseCustomEventId } from '@/helpers/event';
import { useNumberFormatter } from '@/composables/useNumberFormatter';
import { IBotConditionsParsed } from '@/types';

export class BetItem extends AbstractModel {
    public id!: string;
    public botId: number | null = null;
    public orderId: number | null = null;
    public orderCreatedAt!: string;
    public bookmakerId!: number;
    public createdAt!: string;
    public statusId!: BetStatus;
    public eventStartedAt!: string;
    public customEventId!: string;
    public odds!: string;
    public handicap: string | null = null;
    public marketTypeId!: MarketType;
    public eventMarketId!: number;
    public type!: BetType;
    public jollywhere: Jollywhere | null = null;
    public jollywhereClose: Jollywhere | null = null;
    public teamwhere: Teamwhere | null = null;
    public periodTypeId!: EventPeriod;
    public homeWinPercClose: string | null = null;
    public awayWinPercClose: string | null = null;
    public scoreHome: number | null = null;
    public scoreAway: number | null = null;
    public time: string | null = null;
    public timeH: number | null = null;
    public timeMin: number | null = null;
    public situation: string | null = null;
    public hdpVMainOpen: string | null = null;
    public ouVMainOpen: string | null = null;
    public hdpVMainClose: string | null = null;
    public ouVMainClose: string | null = null;
    public wldHomeClose: string | null = null;
    public wldAwayClose: string | null = null;
    public wldDrawClose: string | null = null;
    public marginPerc!: string | null;
    public stake!: string;
    public stakeRequest!: string;
    public balance: string | null = null;
    public wl: WL | null = null;
    public redHome: number | null = null;
    public redAway: number | null = null;
    public topup: number | null = null;
    public correctScore: string | null = null;
    public isLive!: 0 | 1;
    public tradingTypeId!: TradingType;
    public conditions!: string;
    public possessionTimeHome: number | null = null;
    public possessionTimeAway: number | null = null;
    public attacksHome: number | null = null;
    public attacksAway: number | null = null;
    public dangerousAttacksHome: number | null = null;
    public dangerousAttacksAway: number | null = null;
    public shotsHome: number | null = null;
    public shotsAway: number | null = null;
    public shotsOnTargetHome: number | null = null;
    public shotsOnTargetAway: number | null = null;
    public hitWoodworkHome: number | null = null;
    public hitWoodworkAway: number | null = null;
    public foulsHome: number | null = null;
    public foulsAway: number | null = null;
    public cornersHome: number | null = null;
    public cornersAway: number | null = null;
    public scoreHomeFt: number | null = null;
    public scoreAwayFt: number | null = null;
    public scoreHomeHt: number | null = null;
    public scoreAwayHt: number | null = null;
    public scoreHomeIrHt: number | null = null;
    public scoreAwayIrHt: number | null = null;
    public cornersHomeFt: number | null = null;
    public cornersAwayFt: number | null = null;
    public cornersHomeHt: number | null = null;
    public cornersAwayHt: number | null = null;
    public cornersHomeIrHt: number | null = null;
    public cornersAwayIrHt: number | null = null;
    public customTeamHomeName!: string;
    public customTeamAwayName!: string;
    public botName: string | null = null;
    public botDeletedAt: string | null = null;
    public customLeagueId!: number;
    public customLeagueName!: string;
    public iso3!: string;
    public ip: string | null = null;
    public deviceTypeId: DeviceType | null = null;

    constructor() {
        super();
    }

    get statusName(): string {
        return this._lazyLoad('statusName', this.statusId, () => {
            return betStatusDictionary.nameById(this.statusId);
        });
    }

    get marketGroup(): MarketGroup {
        if (this.marketTypeId === MarketType.CornersHandicap || this.marketTypeId === MarketType.CornersGoal) {
            return MarketGroup.Corners;
        }

        return MarketGroup.Regular;
    }

    public displayedEventScoreHome(period: EventPeriod | null = null): number | null {
        if (period === null) {
            period = this.periodTypeId;
        }

        if (this.marketGroup === MarketGroup.Corners) {
            if (period === EventPeriod.FullTime) {
                return this.cornersHomeFt;
            }
            return this.cornersHomeHt;
        }

        if (period === EventPeriod.FullTime) {
            return this.scoreHomeFt;
        }
        return this.scoreHomeHt;
    }

    public displayedEventScoreAway(period: EventPeriod | null = null): number | null {
        if (period === null) {
            period = this.periodTypeId;
        }

        if (this.marketGroup === MarketGroup.Corners) {
            if (period === EventPeriod.FullTime) {
                return this.cornersAwayFt;
            }
            return this.cornersAwayHt;
        }

        if (period === EventPeriod.FullTime) {
            return this.scoreAwayFt;
        }
        return this.scoreAwayHt;
    }

    //Final bet score (Corners or Score, FT or HT)
    public displayedEventScore(period: EventPeriod | null = null): string {
        if (this.displayedEventScoreHome(period) === null) {
            return '';
        }
        return `${this.displayedEventScoreHome(period)}-${this.displayedEventScoreAway(period)}`;
    }

    get displayedScoreHome(): number | null {
        if (!this.isLive) {
            return null;
        }

        return this.marketGroup === MarketGroup.Corners ? this.cornersHome : this.scoreHome;
    }

    get displayedScoreAway(): number | null {
        if (!this.isLive) {
            return null;
        }

        return this.marketGroup === MarketGroup.Corners ? this.cornersAway : this.scoreAway;
    }

    //Bet score (Corners or Score, FT or HT)
    get displayedScore(): string {
        if (this.displayedScoreHome === null) {
            return '';
        }
        return `${this.displayedScoreHome}-${this.displayedScoreAway}`;
    }

    get displayedMinute(): string {
        if (!this.time) {
            return '';
        }
        if (this.time === 'HT' || this.time === 'LIVE') {
            return this.time;
        } else if (this.timeH === 2) {
            const minute = 45 + Number(this.timeMin);
            return (minute <= 90 ? minute : '90+' + (minute - 90)) + "'";
        }

        const minute = Number(this.timeMin);
        return (minute <= 45 ? minute : '45+' + (minute - 45)) + "'";
    }

    public getSelectionName(withValue: boolean = false): string {
        return this._lazyLoad('selectionName', withValue, () => {
            //specific for CS
            if (this.type === BetType.Cs) {
                return withValue ? this.t('betDialog.correctScore') + ' ' + this.correctScore : (this.correctScore as string);
            }

            let result = '';
            if (this.type === BetType.HandicapJolly || this.type === BetType.HandicapDog) {
                result = this.teamwhere === Teamwhere.Home ? this.t('bet.home') : this.t('bet.away');
                result += ' ';
            } else if (this.type === BetType.CornersHandicapJolly || this.type === BetType.CornersHandicapDog) {
                result = this.teamwhere === Teamwhere.Home ? this.t('bet.cornersHome') : this.t('bet.cornersAway');
                result += ' ';
            } else if (this.type === BetType.GoalOver) {
                result = this.t('betDialog.over');
            } else if (this.type === BetType.GoalUnder) {
                result = this.t('betDialog.under');
            } else if (this.type === BetType.CornersGoalOver) {
                result = this.t('betDialog.cornersOver');
            } else if (this.type === BetType.CornersGoalUnder) {
                result = this.t('betDialog.cornersUnder');
            } else if (this.type === BetType.OneXTwoHome) {
                result = this.t('betDialog.home');
            } else if (this.type === BetType.OneXTwoDraw) {
                result = this.t('betDialog.draw');
            } else if (this.type === BetType.OneXTwoAway) {
                result = this.t('betDialog.away');
            } else if (this.type === BetType.BtsYes) {
                result = this.t('bet.bts') + ' - ' + this.t('bet.yes');
            } else if (this.type === BetType.BtsNo) {
                result = this.t('bet.bts') + ' - ' + this.t('bet.no');
            } else if (this.type === BetType.Dc1x) {
                result = this.t('betDialog.home') + this.t('betDialog.or') + this.t('betDialog.draw');
            } else if (this.type === BetType.Dc12) {
                result = this.t('betDialog.home') + this.t('betDialog.or') + this.t('betDialog.away');
            } else if (this.type === BetType.DcX2) {
                result = this.t('betDialog.draw') + this.t('betDialog.or') + this.t('betDialog.away');
            } else {
                throw new AppError(`Unknown betType ${this.type}`);
            }

            if (!withValue) {
                return result;
            }

            if (
                this.marketTypeId === MarketType.Handicap ||
                this.marketTypeId === MarketType.CornersHandicap ||
                this.marketTypeId === MarketType.Goal ||
                this.marketTypeId === MarketType.CornersGoal
            ) {
                if (Number(this.handicap) == 0) {
                    result += ' 0';
                } else {
                    if ((this.type === BetType.HandicapDog || this.type === BetType.CornersHandicapDog) && Number(this.handicap) > 0) {
                        result += ' +';
                    } else {
                        result += ' ';
                    }
                    result += parseFloat(this.handicap as string).toFixed(2);
                }
            }

            return result;
        });
    }

    get missingStake(): string {
        return (Number(this.stake) - Number(this.stakeRequest)).toFixed(2);
    }

    get conditionsParsed(): IBotConditionsParsed {
        return this._lazyLoad('conditionsParsed', this.conditions, () => {
            try {
                const parsed = JSON.parse(this.conditions);
                const normalized: any = {
                    bot: {
                        settings: {},
                    },
                };

                if (parsed.bot) {
                    Object.keys(parsed.bot).forEach((shortKey) => {
                        const fullKey = BOT_OPTIONS_MAP_REVERSE[shortKey] ?? shortKey;
                        normalized.bot[fullKey] = parsed.bot[shortKey];
                    });

                    if (parsed.bot.sts) {
                        Object.keys(parsed.bot.sts).forEach((shortKey) => {
                            const fullKey = BOT_CONDITIONS_MAP_REVERSE[shortKey] ?? shortKey;
                            normalized.bot.settings[fullKey] = parsed.bot.sts[shortKey];
                        });
                    }
                }

                if (parsed.agc) normalized.ageCategory = parsed.agc;
                if (parsed.cat) normalized.category = parsed.cat;
                if (parsed.lcg) normalized.limitsCategory = parsed.lcg;
                if (parsed.clg) normalized.customLeague = parsed.clg;
                if (parsed.clx) normalized.customLeagueExclude = parsed.clx;

                return normalized;
            } catch (e) {
                throw new AppError('Invalid bot condition JSON:', { cause: e });
            }
        });
    }

    public getBot(): BotDictionaryItem | null {
        if (this.botId === null) {
            return null;
        }
        const store = useBotDictionary();
        return store.itemById(this.botId);
    }

    public getBookmaker(): BookmakerDictionaryItem {
        const store = useBookmakerDictionary();

        /*const totalIterations = 100000;
        console.time('itemById benchmark');
        //const map = teamType2Dictionary.map;
        for (let i = 0; i < totalIterations; i++) {
            //store.itemById(this.bookmakerId);
            constants.SITE_NAME[this.bookmakerId];
        }
        console.timeEnd('itemById benchmark');*/

        return store.itemById(this.bookmakerId);
    }

    public isFT(): boolean {
        return this.periodTypeId === EventPeriod.FullTime;
    }

    public is1H(): boolean {
        return this.periodTypeId === EventPeriod.FirstHalf;
    }

    /**
     * Full Time / First Half
     * @param isAbbr
     */
    public getPeriodName(isAbbr: boolean = false): string {
        if (this.isFT()) {
            return isAbbr ? this.t('order.periodFTAbbr') : this.t('order.periodFT');
        }

        return isAbbr ? this.t('order.period1HAbbr') : this.t('order.period1H');
    }

    /**
     * Live / Pregame
     * @param isAbbr
     */
    public getEventTypeName(isAbbr: boolean = false): string {
        if (this.isLive) {
            return isAbbr ? this.t('panel.marketLiveShort') : this.t('panel.marketLive');
        } else {
            return isAbbr ? this.t('panel.marketDballShort') : this.t('panel.marketDball');
        }
    }

    public getMarketTypeName(isAbbr: boolean = false): string {
        return this._lazyLoad('getMarketTypeName', isAbbr, () => {
            switch (this.marketTypeId) {
                case MarketType.Handicap:
                    return isAbbr ? this.t('order.handicapAbbr') : this.t('order.handicap');
                case MarketType.Goal:
                    return isAbbr ? this.t('order.goalAbbr') : this.t('order.over') + '/' + this.t('order.under');
                case MarketType.OneXTwo:
                    return this.t('order.1X2');
                case MarketType.Bts:
                    return isAbbr ? this.t('order.bts') : this.t('order.btts');
                case MarketType.Dc:
                    return isAbbr ? this.t('order.dc') : this.t('order.doubleChance');
                case MarketType.Cs:
                    return isAbbr ? this.t('order.cs') : this.t('order.correctScore');
                case MarketType.CornersHandicap:
                    return isAbbr ? this.t('order.cornersHandicapAbbr') : this.t('order.cornersAsianHandicap');
                case MarketType.CornersGoal:
                    return isAbbr ? this.t('order.cornersGoalAbbr') : this.t('order.cornersOver') + '/' + this.t('order.cornersUnder');
                default:
                    throw new AppError(`Unable to defined marketType #${this.marketTypeId}`);
            }
        });
    }

    public isLiveStats(): boolean {
        return (
            (this.possessionTimeHome !== null && this.possessionTimeAway !== null) ||
            (this.attacksHome !== null && this.attacksAway !== null) ||
            (this.dangerousAttacksHome !== null && this.dangerousAttacksAway !== null) ||
            (this.shotsHome !== null && this.shotsAway !== null) ||
            (this.shotsOnTargetHome !== null && this.shotsOnTargetAway !== null) ||
            (this.hitWoodworkHome !== null && this.hitWoodworkAway !== null) ||
            (this.foulsHome !== null && this.foulsAway !== null) ||
            (this.cornersHome !== null && this.cornersAway !== null)
        );
    }

    get betMinute(): string {
        if (this.time && (this.time === 'HT' || this.time.toUpperCase() === 'LIVE')) {
            return this.time;
        } else if (this.timeH === 2) {
            let minute = 45 + Number(this.timeMin);
            return (minute <= 90 ? minute : '90+' + (minute - 90)) + "'";
        }

        return (Number(this.timeMin) <= 45 ? this.timeMin : '45+' + (Number(this.timeMin) - 45)) + "'";
    }
    get betTooltip(): string {
        const bookmakerDictionary = useBookmakerDictionary();
        const platform = usePlatform();
        const { formatNumber } = useNumberFormatter();

        let betInfo = '';
        let eventInfo = '';
        let score = '';
        let market = '';
        let bookmaker = platform.isAggregator() ? bookmakerDictionary.nameById(this.bookmakerId) : '';
        let minute = this.time ? ' ' + this.betMinute + ' ' : ' ';

        let betType = '';
        let game = this.customTeamHomeName + ' vs ' + this.customTeamAwayName;

        let period = (this.periodTypeId === EventPeriod.FullTime ? this.t('panel.marketFT') : this.t('panel.market1H')) + ' | ';
        let handicap = parseFloat(this.handicap ?? '0').toFixed(2);
        const userStore = useUserStore();

        if (handicap === '0.00') {
            handicap = '0';
        }

        let odds = ' @' + formatOdds(this.odds);
        let stake = userStore.currency.symbol + formatNumber(this.stake, 'money');

        if (this.isLive) {
            if (this.type === BetType.CornersHandicapJolly || this.type === BetType.CornersHandicapDog || this.type === BetType.CornersGoalOver || this.type === BetType.CornersGoalUnder) {
                score += ' (' + (this.cornersHome !== null ? this.cornersHome + '-' + this.cornersAway : this.t('order.cornersNC')) + ')';
            } else {
                score = ' (' + (this.scoreHome !== null ? this.scoreHome + '-' + this.scoreAway : this.t('order.NC')) + ')';
            }
            market = this.t('panel.marketLive') + ' | ';
        } else {
            market = this.t('panel.marketDball') + ' | ';
        }

        if (this.type === BetType.OneXTwoHome) {
            betInfo = this.customTeamHomeName + ' ' + odds + score;
            betType = '1X2';
        } else if (this.type === BetType.OneXTwoAway) {
            betInfo = this.customTeamAwayName + ' ' + odds + score;
            betType = '1X2';
        } else if (this.type === BetType.OneXTwoDraw) {
            betInfo = this.t('bet.popup.draw') + odds + score;
            betType = '1X2';
        } else if (this.type === BetType.HandicapJolly || this.type === BetType.HandicapDog) {
            let team = this.teamwhere === Teamwhere.Home ? this.customTeamHomeName : this.customTeamAwayName;
            betInfo = team + ' ' + (Number(handicap) > 0 ? '+' : '') + handicap + odds + score;
            betType = this.t('panel.asianHandicap') + ' ';
        } else if (this.type === BetType.CornersHandicapJolly || this.type === BetType.CornersHandicapDog) {
            let team = this.teamwhere === Teamwhere.Home ? this.customTeamHomeName : this.customTeamAwayName;
            betInfo = team + ' ' + (Number(handicap) > 0 ? '+' : '') + handicap + odds + score;
            betType = this.t('panel.cornersAsianHandicap') + ' ';
        } else if (this.type === BetType.GoalOver) {
            betInfo = this.t('betDialog.over') + ' ' + handicap + odds + score;
            betType = this.t('panel.goal');
        } else if (this.type === BetType.GoalUnder) {
            betInfo = this.t('betDialog.under') + ' ' + handicap + odds + score;
            betType = this.t('panel.goal');
        } else if (this.type === BetType.CornersGoalOver) {
            betInfo = this.t('betDialog.over') + ' ' + handicap + odds + score;
            betType = this.t('panel.cornersGoal');
        } else if (this.type === BetType.CornersGoalUnder) {
            betInfo = this.t('betDialog.under') + ' ' + handicap + odds + score;
            betType = this.t('panel.cornersGoal');
        } else if (this.type === BetType.BtsYes) {
            betInfo = this.t('betDialog.yes') + ' ' + odds + score;
            betType = this.t('betDialog.btts');
        } else if (this.type === BetType.BtsNo) {
            betInfo = this.t('betDialog.no') + ' ' + odds + score;
            betType = this.t('betDialog.btts');
        } else if (this.type === BetType.Dc1x) {
            betInfo = this.t('betDialog.home') + this.t('betDialog.or') + this.t('betDialog.draw') + ' ' + odds + score;
            betType = this.t('betDialog.doubleChance');
        } else if (this.type === BetType.Dc12) {
            betInfo = this.t('betDialog.home') + this.t('betDialog.or') + this.t('betDialog.away') + ' ' + odds + score;
            betType = this.t('betDialog.doubleChance');
        } else if (this.type === BetType.DcX2) {
            betInfo = this.t('betDialog.draw') + this.t('betDialog.or') + this.t('betDialog.away') + ' ' + odds + score;
            betType = this.t('betDialog.doubleChance');
        } else if (this.type === BetType.Cs) {
            betInfo = this.correctScore + ' ' + odds + score;
            betType = this.t('betDialog.correctScore');
        }

        eventInfo =
            this.customLeagueName +
            '<br />' +
            (!this.isLive && this.eventStartedAt ? dateFormatInUserTimeZone(this.eventStartedAt, 'dd MMM H:mm') + '<br />' : '') +
            game +
            '<br />' +
            market +
            period +
            betType +
            '<br />' +
            betInfo +
            minute +
            // betting +
            bookmaker +
            ' ' +
            stake;

        return eventInfo;
    }

    public bettingInfo(withStake = true, stakeClass = 'float-end', withSite = false, withTeam = true): { betInfo: string; eventInfo: string } {
        let betInfo = '';
        let eventInfo = '';
        let score = '';
        let game = withTeam ? ' [' + this.t('bet.game') + ']' : '';
        let period = (withTeam ? ' • ' : '') + this.getPeriodName(true);

        const userStore = useUserStore();
        const { formatNumber } = useNumberFormatter();

        let handicap = parseFloat(this.handicap ?? '0').toFixed(2);

        if (handicap === '0.00') {
            handicap = '0';
        }

        let site = withSite ? '<span class="pr-1 bookmaker' + this.bookmakerId + '">' + BookmakerCode[this.bookmakerId] + '</span>' : '';

        let odds = ' @' + formatOdds(this.odds);

        let stake = '';

        if (withStake) {
            if (this.statusId === BetStatus.Settled && this.balance) {
                const colorClass = Number(this.balance) > 0 ? 'greenText' : Number(this.balance) < 0 ? 'redText' : '';
                stake =
                    ' <span style="text-decoration: inherit;" class="' + stakeClass + ' ' + colorClass + ' pr-1">' + site + userStore.currency.symbol + formatNumber(this.balance, 'money') + '</span>';
            } else if (this.stake) {
                stake = ' <span style="text-decoration: inherit;" class="' + stakeClass + ' pr-1">' + site + userStore.currency.symbol + formatNumber(this.stake, 'money') + '</span>';
            }
        }

        if (this.isLive) {
            score = ' ' + this.displayedScore;
        }

        if (this.type === BetType.OneXTwoHome) {
            betInfo = (withTeam ? '' : this.t('bet.popup.home')) + odds;
            eventInfo = (withTeam ? '<div class="text-truncate panel-bet pr-1">' + this.customTeamHomeName + '</div>' : '') + period + '|1X2';
        } else if (this.type === BetType.OneXTwoAway) {
            betInfo = (withTeam ? '' : this.t('bet.popup.away')) + odds;
            eventInfo = (withTeam ? '<div class="text-truncate panel-bet pr-1">' + this.customTeamAwayName + '</div>' : '') + period + '|1X2';
        } else if (this.type === BetType.OneXTwoDraw) {
            betInfo = this.t('bet.popup.draw') + odds;
            eventInfo = (withTeam ? '<div class="text-truncate panel-bet pr-1">' + this.customTeamHomeName + '</div>' : '') + period + '|1X2';
        } else if (this.type === BetType.HandicapJolly || this.type === BetType.HandicapDog) {
            betInfo = (Number(handicap) > 0 ? '+' : '') + handicap + odds;
            eventInfo = '<div class="text-truncate panel-bet pr-1">' + (this.teamwhere === Teamwhere.Home ? this.customTeamHomeName : this.customTeamAwayName) + '</div>' + period + '|A/H';
        } else if (this.type === BetType.CornersHandicapJolly || this.type === BetType.CornersHandicapDog) {
            betInfo = (Number(handicap) > 0 ? '+' : '') + handicap + odds;
            eventInfo =
                '<div class="text-truncate panel-bet pr-1">' +
                (this.teamwhere === Teamwhere.Home ? this.customTeamHomeName : this.customTeamAwayName) +
                '</div>' +
                period +
                '|' +
                this.t('order.cornersHandicapAbbr');
        } else if (this.type === BetType.GoalOver) {
            betInfo = this.t('betDialog.over') + ' ' + handicap + odds;
            eventInfo = (withTeam ? '<div class="text-truncate panel-bet pr-1">' + this.customTeamHomeName + '</div>' : '') + game + period + '|O/U';
        } else if (this.type === BetType.GoalUnder) {
            betInfo = this.t('betDialog.under') + ' ' + handicap + odds;
            eventInfo = (withTeam ? '<div class="text-truncate panel-bet pr-1">' + this.customTeamHomeName + '</div>' : '') + game + period + '|O/U';
        } else if (this.type === BetType.CornersGoalOver) {
            betInfo = this.t('betDialog.over') + ' ' + handicap + odds;
            eventInfo = (withTeam ? '<div class="text-truncate panel-bet pr-1">' + this.customTeamHomeName + '</div>' : '') + game + period + '|' + this.t('order.cornersGoalAbbr');
        } else if (this.type === BetType.CornersGoalUnder) {
            betInfo = this.t('betDialog.under') + ' ' + handicap + odds;
            eventInfo = (withTeam ? '<div class="text-truncate panel-bet pr-1">' + this.customTeamHomeName + '</div>' : '') + game + period + '|' + this.t('order.cornersGoalAbbr');
        } else if (this.type === BetType.BtsYes) {
            betInfo = this.t('betDialog.bts') + ' - ' + this.t('betDialog.yes') + odds;
            eventInfo = (withTeam ? '<div class="text-truncate panel-bet pr-1">' + this.customTeamHomeName + '</div>' : '') + game + period + '|' + this.t('order.bts');
        } else if (this.type === BetType.BtsNo) {
            betInfo = this.t('betDialog.bts') + ' - ' + this.t('betDialog.no') + odds;
            eventInfo = (withTeam ? '<div class="text-truncate panel-bet pr-1">' + this.customTeamHomeName + '</div>' : '') + game + period + '|' + this.t('order.bts');
        } else if (this.type === BetType.Dc1x) {
            betInfo = this.t('betDialog.home') + this.t('betDialog.or') + this.t('betDialog.draw') + odds;
            eventInfo = (withTeam ? '<div class="text-truncate panel-bet pr-1">' + this.customTeamHomeName + '</div>' : '') + game + period + '|' + this.t('order.dc');
        } else if (this.type === BetType.Dc12) {
            betInfo = this.t('betDialog.home') + this.t('betDialog.or') + this.t('betDialog.away') + odds;
            eventInfo = (withTeam ? '<div class="text-truncate panel-bet pr-1">' + this.customTeamHomeName + '</div>' : '') + game + period + '|' + this.t('order.dc');
        } else if (this.type === BetType.DcX2) {
            betInfo = this.t('betDialog.draw') + this.t('betDialog.or') + this.t('betDialog.away') + odds;
            eventInfo = (withTeam ? '<div class="text-truncate panel-bet pr-1">' + this.customTeamHomeName + '</div>' : '') + game + period + '|' + this.t('order.dc');
        } else if (this.type === BetType.Cs) {
            betInfo = this.correctScore + odds;
            eventInfo = '<div class="text-truncate panel-bet pr-1">' + this.customTeamHomeName + '</div>' + period + '|' + this.t('order.cs');
        }

        betInfo += score + stake;

        return { betInfo: betInfo, eventInfo: eventInfo };
    }

    get eventName() {
        const platform = usePlatform();
        let info = this.bettingInfo(true, 'float-end', platform.isAggregator());
        return info.eventInfo + '<br>' + info.betInfo;
    }

    get eventNameNoTeam() {
        const platform = usePlatform();
        let info = this.bettingInfo(true, 'float-end', platform.isAggregator(), false);
        return info.eventInfo + '<br>' + info.betInfo;
    }

    public showAddMessage() {
        const userStore = useUserStore();

        if (userStore.settings.is_show_popup_on_bet) {
            let info = this.bettingInfo(true, '');
            let msg = (this.botId ? this.t('bet.popup.botName', { name: this.botName }) : this.t('bet.popup.betOrderId', { id: this.orderId })) + '<br>' + info.eventInfo + '<br>' + info.betInfo;
            if (this.statusId === BetStatus.Waiting) {
                newBetWaitingMessage(msg);
            } else {
                newBetMessage(msg);
            }
        }
        if (userStore.settings.is_play_sound_on_bet && !document.hidden) {
            playSound('/audio/coins-falling-022-37390.mp3');
        }
    }

    public calculateExpectedResult(finalScoreHome: number, finalScoreAway: number): IExpectedResult | null {
        if (this.isLive && (this.displayedScoreHome === null || this.displayedScoreAway === null)) {
            return null;
        }

        const betScoreHome = this.isLive ? this.displayedScoreHome : 0;
        const betScoreAway = this.isLive ? this.displayedScoreAway : 0;

        // if (finalScoreHome < this.displayedScoreHome || finalScoreAway < this.displayedScoreAway) {
        //     return null;
        // }

        let wl, validStake, balance;

        if (this.wl) {
            wl = this.wl;
        }

        if (!wl) {
            if (
                this.marketTypeId === MarketType.Handicap ||
                this.marketTypeId === MarketType.Goal ||
                this.marketTypeId === MarketType.CornersHandicap ||
                this.marketTypeId === MarketType.CornersGoal
            ) {
                let diff;
                if (this.marketTypeId === MarketType.Handicap || this.marketTypeId === MarketType.CornersHandicap) {
                    finalScoreHome = finalScoreHome - betScoreHome!;
                    finalScoreAway = finalScoreAway - betScoreAway!;
                    if (this.teamwhere === Teamwhere.Home) {
                        finalScoreHome += Number(this.handicap);
                        diff = finalScoreHome - finalScoreAway;
                    } else {
                        finalScoreAway += Number(this.handicap);
                        diff = finalScoreAway - finalScoreHome;
                    }
                } else {
                    //goals
                    let goals = finalScoreHome + finalScoreAway;
                    if (this.teamwhere === Teamwhere.Home) {
                        diff = goals - Number(this.handicap);
                    } else {
                        diff = Number(this.handicap) - goals;
                    }
                }

                if (diff >= 0.5) {
                    wl = WL.W;
                } else if (diff == 0.25) {
                    wl = WL.HW;
                } else if (diff == 0) {
                    wl = WL.P;
                } else if (diff == -0.25) {
                    wl = WL.HL;
                } else if (diff <= -0.5) {
                    wl = WL.L;
                } else {
                    throw new AppError(`Error during balance calculation, DIFF is ${diff}`);
                }
            } else if (this.type === BetType.OneXTwoHome) {
                wl = finalScoreHome > finalScoreAway ? WL.W : WL.L;
            } else if (this.type === BetType.OneXTwoAway) {
                wl = finalScoreHome < finalScoreAway ? WL.W : WL.L;
            } else if (this.type === BetType.OneXTwoDraw) {
                wl = finalScoreHome === finalScoreAway ? WL.W : WL.L;
            } else if (this.type === BetType.BtsYes) {
                wl = finalScoreHome > 0 && finalScoreAway > 0 ? WL.W : WL.L;
            } else if (this.type === BetType.BtsNo) {
                wl = finalScoreHome > 0 && finalScoreAway > 0 ? WL.L : WL.W;
            } else if (this.type === BetType.Dc1x) {
                wl = finalScoreHome >= finalScoreAway ? WL.W : WL.L;
            } else if (this.type === BetType.Dc12) {
                wl = finalScoreHome > finalScoreAway || finalScoreAway > finalScoreHome ? WL.W : WL.L;
            } else if (this.type === BetType.DcX2) {
                wl = finalScoreAway >= finalScoreHome ? WL.W : WL.L;
            } else if (this.type === BetType.Cs) {
                const [homeCorrect, awayCorrect] = this.correctScore!.split('-').map(Number);
                wl = finalScoreHome === homeCorrect && finalScoreAway === awayCorrect ? WL.W : WL.L;
            }
        }

        switch (wl) {
            case 'P':
                balance = validStake = 0;
                break;
            case 'W':
                balance = Number(this.stake) * Number(this.odds) - Number(this.stake);
                validStake = Math.abs(balance);
                break;
            case 'HW':
                balance = (Number(this.stake) * Number(this.odds) - Number(this.stake)) / 2;
                validStake = Math.abs(balance);
                break;
            case 'L':
                balance = 0 - Number(this.stake);
                validStake = Math.abs(balance);
                break;
            case 'HL':
                balance = 0 - Number(this.stake) / 2;
                validStake = Math.abs(balance);
                break;
        }

        if (wl === undefined || balance === undefined || validStake === undefined) {
            throw new AppError(`Error during balance calculation, wl/balance/validStake is undefined for bet type ${this.type}`);
        }

        return { wl: wl, balance: balance.toFixed(2), validStake: validStake.toFixed(2) };
    }

    public sendMessageToWL() {
        const [date, customTeamHomeId, customTeamAwayId] = parseCustomEventId(this.customEventId);
        const yy = date.substring(2, 4);
        const mm = date.substring(5, 7);
        const dd = date.substring(8, 10);

        const eventId = `${yy}${mm}${dd}${customTeamHomeId}${customTeamAwayId}`;

        let selectionName = '';
        switch (this.type) {
            case BetType.HandicapJolly:
            case BetType.HandicapDog:
            case BetType.OneXTwoHome:
            case BetType.OneXTwoAway:
                selectionName = this.teamwhere === Teamwhere.Home ? 'Home' : 'Away';
                break;
            case BetType.GoalOver:
                selectionName = 'Over';
                break;
            case BetType.GoalUnder:
                selectionName = 'Under';
                break;
            case BetType.OneXTwoDraw:
                selectionName = 'Draw';
                break;
            case BetType.BtsYes:
                selectionName = 'Yes';
                break;
            case BetType.BtsNo:
                selectionName = 'No';
                break;
            case BetType.Dc1x:
                selectionName = '1X';
                break;
            case BetType.Dc12:
                selectionName = '12';
                break;
            case BetType.DcX2:
                selectionName = 'X2';
                break;
            case BetType.CornersHandicapJolly:
            case BetType.CornersHandicapDog:
                selectionName = this.teamwhere === Teamwhere.Home ? 'Corners Home' : 'Corners Away';
                break;
            case BetType.CornersGoalOver:
                selectionName = 'Corners Over';
                break;
            case BetType.CornersGoalUnder:
                selectionName = 'Corners Under';
                break;
            case BetType.Cs:
                selectionName = this.correctScore!;
                break;
            default:
                throw new AppError(`Unable to define bet type ${this.type}`);
        }

        let marketName = this.isLive ? 'LV | ' : 'PG | ';
        marketName += this.isFT() ? 'FT | ' : '1H | ';

        switch (this.marketTypeId) {
            case MarketType.Handicap:
                marketName += 'A/H';
                break;
            case MarketType.Goal:
                marketName += 'O/U';
                break;
            case MarketType.OneXTwo:
                marketName += '1X2';
                break;
            case MarketType.Bts:
                marketName += 'BTS';
                break;
            case MarketType.Dc:
                marketName += 'DC';
                break;
            case MarketType.Cs:
                marketName += 'CS';
                break;
            case MarketType.CornersHandicap:
                marketName += 'C-A/H';
                break;
            case MarketType.CornersGoal:
                marketName += 'C-O/U';
                break;
            default:
                throw new AppError(`Unable to define bet market type #${this.marketTypeId}`);
        }

        let message = JSON.stringify({
            type: 'betPlaced',
            data: {
                betId: this.id,
                sportId: '',
                sportName: 'Soccer',
                competitionId: this.customLeagueId,
                competitionName: this.customLeagueName,
                eventId: eventId,
                eventName: `${this.customTeamHomeName} v ${this.customTeamAwayName}`,
                marketId: this.eventMarketId,
                marketName: marketName,
                selectionId: '',
                selectionName: selectionName,
                handicap: this.handicap,
                odds: this.odds,
                stake: this.stake, // stake is the liability, what player can lose
                marketStartTime: londonToGMT(this.eventStartedAt),
            },
        });

        console.log('Send msg to parent: ' + message);
        window.parent.postMessage(message, '*');
    }
}
